// tslint:disable: variable-name

import {
  AfterContentInit,
  ContentChildren,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
  HostBinding,
  AfterViewInit
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { ButtonRadio } from './button-radio.component';
import { ButtonRadioChange } from './button-radio-change.class';

@Component({
  selector: 'suka-button-radio-group',
  template: `
    <div
      class="button-radio-group"
      role="buttonRadiogroup">
      <ng-content></ng-content>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ButtonRadioGroup,
      multi: true
    }
  ]
})
// tslint:disable-next-line: component-class-suffix
export class ButtonRadioGroup implements AfterContentInit, AfterViewInit, ControlValueAccessor {
  static buttonRadioGroupCount = 0;

  /**
   * Emits event notifying other classes of a change using a `ButtonRadioChange` class.
   */
  // tslint:disable-next-line: no-output-native
  @Output() change: EventEmitter<ButtonRadioChange> = new EventEmitter<ButtonRadioChange>();

  /**
   * The `ButtonRadio` input items in the `ButtonRadioGroup`.
   */
  @ContentChildren(forwardRef(() => ButtonRadio)) buttonRadios: QueryList<ButtonRadio>;

  /**
   * Sets the passed in `ButtonRadio` item as the selected input within the `ButtonRadioGroup`.
   */
  @Input()
  set selected(selected: ButtonRadio | null) {
    this._selected = selected;
    this.value = selected ? selected.value : null;
    this.checkSelectedButtonRadio();
  }

  /**
   * Returns the `ButtonRadio` that is selected within the `ButtonRadioGroup`.
   */
  get selected() {
    return this._selected;
  }

  /**
   * Sets the value/state of the selected `ButtonRadio` within the `ButtonRadioGroup` to the passed in value.
   */
  @Input()
  set value(newValue: any) {
    if (this._value !== newValue) {
      this._value = newValue;

      this.updateSelectedButtonRadioFromValue();
      this.checkSelectedButtonRadio();
    }
  }

  /**
   * Returns the value/state of the selected `ButtonRadio` within the `ButtonRadioGroup`.
   */
  get value() {
    return this._value;
  }

  /**
   * Replaces the name associated with the `ButtonRadioGroup` with the provided parameter.
   */
  @Input()
  set name(name: string) {
    this._name = name;
    this.updateButtonRadios();
  }
  /**
   * Returns the associated name of the `ButtonRadioGroup`.
   */
  get name() {
    return this._name;
  }

  /**
   * Set to true to disable the whole buttonRadio group
   */
  @Input() disabled = false;

  /**
   * Returns the skeleton value in the `ButtonRadioGroup` if there is one.
   */
  @Input()
  get skeleton(): any {
    return this._skeleton;
  }

  /**
   * Sets the skeleton value for all `ButtonRadio` to the skeleton value of `ButtonRadioGroup`.
   */
  set skeleton(value: any) {
    this._skeleton = value;
    this.updateChildren();
  }

  /**
   * Binds 'form-item' value to the class for `ButtonRadioGroup`.
   */
  @HostBinding('class.form-item') buttonRadioButtonGroupClass = true;

  /**
   * To track whether the `ButtonRadioGroup` has been initialized.
   */
  protected isInitialized = false;
  /**
   * Reflects whether or not the input is disabled and cannot be selected.
   */
  protected _disabled = false;
  /**
   * Reflects whether or not the dropdown is loading.
   */
  protected _skeleton = false;
  /**
   * The value of the selected option within the `ButtonRadioGroup`.
   */
  protected _value: any = null;
  /**
   * The `ButtonRadio` within the `ButtonRadioGroup` that is selected.
   */
  protected _selected: ButtonRadio = null;
  /**
   * The name attribute associated with the `ButtonRadioGroup`.
   */
  protected _name = `button-radio-group-${ButtonRadioGroup.buttonRadioGroupCount++}`;

  /**
   * Updates the selected `ButtonRadio` to be checked (selected).
   */
  checkSelectedButtonRadio() {
    if (this.selected && !this._selected.checked) {
      this.selected.checked = true;
    }
  }

  /**
   * Use the value of the `ButtonRadioGroup` to update the selected buttonRadio to the right state (selected state).
   */
  updateSelectedButtonRadioFromValue() {
    const alreadySelected = this._selected != null && this._selected.value === this._value;

    if (this.buttonRadios && !alreadySelected) {
      this._selected = null;
      this.buttonRadios.forEach(buttonRadio => {
        if (buttonRadio.checked) {
          this._selected = buttonRadio;
        }
      });
    }
  }

  /**
   * Creates a class of `ButtonRadioChange` to emit the change in the `ButtonRadioGroup`.
   */
  emitChangeEvent(event: ButtonRadioChange) {
    this.change.emit(event);
    this.propagateChange(event.value);
    this.onTouched();
  }

  /**
   * Synchronizes buttonRadio properties.
   */
  updateButtonRadios() {
    if (this.buttonRadios) {
      setTimeout(() => {
        this.buttonRadios.forEach(buttonRadio => buttonRadio.name = this.name);
      });
    }
  }

  /**
   * Updates the value of the `ButtonRadioGroup` using the provided parameter.
   */
  writeValue(value: any) {
    this.value = value;
  }

  ngAfterContentInit() {
    this.buttonRadios.changes.subscribe(() => {
      this.updateButtonRadios();
      this.updateButtonRadioChangeHandler();
    });

    this.updateChildren();
    this.updateButtonRadioChangeHandler();
  }

  ngAfterViewInit() {
    this.updateButtonRadios();
  }

  /**
   * Used to set method to propagate changes back to the form.
   */
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the checkbox is touched.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Needed to properly implement ControlValueAccessor.
   */
  onTouched: () => any = () => { };

  /**
   * Method set in registerOnChange to propagate changes back to the form.
   */
  propagateChange = (_: any) => { };

  protected updateChildren() {
    if (this.buttonRadios) {
      this.buttonRadios.forEach(child => child.skeleton = this.skeleton);
    }
  }

  protected updateButtonRadioChangeHandler() {
    this.buttonRadios.forEach(buttonRadio => {
      buttonRadio.registerButtonRadioChangeHandler((event: ButtonRadioChange) => {
        // update selected and value from the event
        this._selected = event.source;
        this._value = event.value;
        // bubble the event
        this.emitChangeEvent(event);
      });
    });
  }
}
